import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/*
 * Action.
 *
 * Primary is ink and performs the next accountable action. Outline and ghost
 * open detail or an alternative. `brand` is the blue variant, kept for
 * surfaces outside the employee portal that still lead with it.
 *
 * A disabled action has to say why: the guide treats a control that is blocked
 * without explanation as a defect, so `reason` is surfaced rather than
 * dropped, and a disabled action with no reason is a bug in the caller.
 */

type Variant = 'primary' | 'brand' | 'outline' | 'quiet' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

const VARIANT: Record<Variant, string> = {
  primary: 'bg-ink text-white hover:bg-black',
  brand: 'bg-brand-500 text-white hover:bg-brand-600',
  outline: 'border border-line-strong bg-white text-ink hover:bg-canvas-inset',
  quiet: 'border border-line bg-white text-ink hover:bg-canvas-inset',
  ghost: 'bg-transparent text-ink-muted hover:bg-canvas-inset hover:text-ink',
  danger: 'bg-danger text-white hover:brightness-95',
};

/*
 * Three heights, each from the audit's control table: 46px for the action that
 * carries the screen, 40px for a topbar or secondary control, and the in-row
 * variant that has no fixed height because it sits on a row's own baseline.
 */
const SIZE: Record<Size, string> = {
  sm: 'gap-1.5 px-2.5 py-[5px] text-[12px]',
  md: 'h-10 gap-2 px-4 text-[13px]',
  lg: 'h-[46px] gap-2 px-[18px] text-[13px]',
};

export interface ActionProps {
  variant?: Variant;
  size?: Size;
  arrow?: boolean;
  icon?: React.ReactNode;
  to?: string;
  /** Router state carried with `to`, e.g. where a full-screen page should return to. */
  state?: unknown;
  href?: string;
  onClick?: () => void;
  disabled?: boolean;
  reason?: string;
  type?: 'button' | 'submit';
  className?: string;
  children: React.ReactNode;
}

export const Action: React.FC<ActionProps> = ({
  variant = 'primary', size = 'md', arrow = false, icon, to, state, href, onClick,
  disabled = false, reason, type = 'button', className = '', children,
}) => {
  const base = `inline-flex items-center justify-center whitespace-nowrap rounded-act font-semibold leading-none transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ink/15 ${SIZE[size]} ${VARIANT[variant]} ${className}`;
  const body = (
    <>
      {icon}
      {children}
      {arrow && <ArrowRight size={14} />}
    </>
  );

  if (disabled) {
    return (
      <button type="button" disabled title={reason} className={`${base} cursor-not-allowed opacity-45`}>
        {body}
      </button>
    );
  }
  if (to) return <Link to={to} state={state} className={base}>{body}</Link>;
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={base}>{body}</a>;
  return <button type={type} onClick={onClick} className={base}>{body}</button>;
};
